<template>
  <div>
    <!-- Ticket-category picker for the tickets step. Deliberately reuses the
         `.attend-option` card styling from lp-attend-toggle-inline (those rules
         are global, not scoped) so the two selectors that can sit side by side
         in the step header read as one control family. On phones the cards
         collapse into a segmented control (see the 640px block below). -->
    <div
      class="category-select"
      :class="{ 'category-select--scroll': categories.length > 2 }"
      role="radiogroup"
      aria-label="Registration type"
    >
      <div
        v-for="c in categories"
        :key="c.id"
        class="attend-option"
        :class="{ selected: isSelected(c) }"
        role="radio"
        :aria-checked="isSelected(c) ? 'true' : 'false'"
        tabindex="0"
        @click="select(c)"
        @keydown.enter.prevent="select(c)"
        @keydown.space.prevent="select(c)"
      >
        <div class="ic">
          <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.6 13.4 12.2 5A2 2 0 0 0 10.8 4.4H5a1 1 0 0 0-1 1v5.8a2 2 0 0 0 .6 1.4l8.4 8.4a2 2 0 0 0 2.8 0l4.8-4.8a2 2 0 0 0 0-2.8z"/><circle cx="8" cy="8" r="1.2" fill="currentColor" stroke="none"/></svg>
        </div>
        <div>
          <div class="label">
            {{ c.title }}
            <!-- Phone-only extras: selected-ticket count and price line. -->
            <span v-if="c.count" class="count" :aria-label="`${c.count} selected`">{{ c.count }}</span>
          </div>
          <div v-if="priceLine(c)" class="sub">{{ priceLine(c) }}</div>
          <div v-if="c.description" class="desc">{{ c.description }}</div>
        </div>
      </div>
    </div>

    <!-- Phones: the cards drop their descriptions, so the selected category's
         description shows here on one line; longer text opens in a drawer. -->
    <div v-if="selected && selected.description" class="category-desc">
      <span ref="descText" class="category-desc__text">{{ selected.description }}</span>
      <button
        v-if="descOverflows"
        type="button"
        class="category-desc__more"
        @click="showDetails = true"
      >Details</button>
    </div>

    <lp-drawer v-if="showDetails" :show-back="false" @close="showDetails = false" @back="showDetails = false">
      <div class="category-details">
        <div class="category-details__eyebrow">Registration type</div>
        <h3 class="category-details__title">{{ selected.title }}</h3>
        <p class="category-details__text">{{ selected.description }}</p>
      </div>
    </lp-drawer>
  </div>
</template>

<script>
export default {
  name: 'lp-category-select',
  props: {
    // Category id; 0 is the "Other" bucket, null means nothing picked yet.
    value: { type: Number, default: null },
    // [{ id, title, description, count, minPrice, maxPrice }] — id 0 marks the
    // uncategorized bucket; count/prices feed the phone segmented control.
    categories: { type: Array, required: true },
    currencySymbol: { type: String, default: '$' },
  },
  data() {
    return {
      descOverflows: false,
      showDetails: false,
    }
  },
  computed: {
    selected() {
      return this.categories.find(c => c.id === this.value) || null
    },
  },
  watch: {
    selected() {
      this.showDetails = false
      this.$nextTick(this.measureDesc)
    },
  },
  mounted() {
    this.measureDesc()
    if (typeof ResizeObserver !== 'undefined') {
      this._descObserver = new ResizeObserver(() => this.measureDesc())
      this._descObserver.observe(this.$el)
    }
  },
  beforeDestroy() {
    if (this._descObserver) this._descObserver.disconnect()
  },
  methods: {
    isSelected(c) {
      return this.value === c.id
    },
    select(c) {
      if (c.id !== this.value) this.$emit('input', c.id)
    },
    priceLine(c) {
      if (c.minPrice == null) return ''
      const min = `${this.currencySymbol}${c.minPrice.toLocaleString()}`
      return c.maxPrice > c.minPrice ? `from ${min}` : min
    },
    // The line is clamped on phones (hidden on desktop, where it measures 0).
    measureDesc() {
      const el = this.$refs.descText
      this.descOverflows = !!el && el.scrollHeight > el.clientHeight + 1
    },
  },
}
</script>

<style lang="scss">
.category-select {
  display: grid;
  /* Wraps to as many columns as fit, unlike the fixed two-up attend toggle —
     an event can define any number of categories. */
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
  margin-top: 14px;
  /* Same cap as the attend toggle above it, so the two selectors line up. */
  max-width: 560px;
}
.category-select .attend-option { padding: 10px 14px; }
.category-select .attend-option .ic { width: 32px; height: 32px; }
.category-select .attend-option .label { font-size: var(--fs-small); }
.category-select .attend-option .desc { font-size: var(--fs-caption); }
/* Phone-only pieces. */
.category-select .attend-option .count,
.category-select .attend-option .sub,
.category-desc { display: none; }

/* Phones: a segmented control (as in templates/mobile-ticket-picker.html)
   instead of tall description cards. */
@media (max-width: 640px) {
  .category-select {
    grid-template-columns: none;
    grid-auto-flow: column;
    grid-auto-columns: minmax(0, 1fr);
    gap: 4px;
    padding: 4px;
    max-width: none;
    background: var(--surface-alt);
    border-radius: 12px;
  }
  /* 3+ categories: a swipeable strip rather than squeezed columns. */
  .category-select--scroll {
    grid-auto-columns: minmax(42%, 1fr);
    overflow-x: auto;
    scrollbar-width: none;
  }
  .category-select--scroll::-webkit-scrollbar { display: none; }

  .category-select .attend-option {
    min-height: 52px;
    padding: 6px 4px;
    border: 0;
    border-radius: 9px;
    background: transparent;
    color: var(--text-secondary);
    justify-content: center;
    text-align: center;
    transition: background 0.15s, box-shadow 0.15s;
  }
  .category-select .attend-option:hover { background: transparent; }
  .category-select .attend-option.selected {
    background: var(--surface);
    color: var(--text);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18);
  }
  .category-select .attend-option .ic,
  .category-select .attend-option .desc { display: none; }
  .category-select .attend-option .label {
    font-size: var(--fs-small);
    font-weight: 600;
    line-height: 1.25;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .category-select .attend-option .count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    background: var(--brand);
    color: #fff;
    font-size: var(--fs-micro);
  }
  .category-select .attend-option .sub {
    display: block;
    margin-top: 2px;
    font-size: var(--fs-caption);
    color: var(--text-tertiary);
  }
  .category-select .attend-option:focus-visible { outline: 2px solid var(--brand); outline-offset: 2px; }

  .category-desc {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-top: 8px;
    font-size: var(--fs-caption);
    color: var(--text-secondary);
  }
  /* Line-clamped rather than nowrap: a nowrap line would widen the page's
     1fr phone grid track to the full text length. */
  .category-desc__text {
    flex: 1;
    min-width: 0;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .category-desc__more {
    flex: none;
    padding: 0;
    border: 0;
    background: none;
    font-family: inherit;
    font-size: var(--fs-caption);
    font-weight: 600;
    color: var(--brand-deep);
    text-decoration: underline;
    cursor: pointer;
  }
}

/* Drawer body (teleported to <body>, so these can't be nested above). */
.category-details { padding: 4px 0 16px; }
.category-details__eyebrow {
  font-size: var(--fs-caption);
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-tertiary);
}
.category-details__title {
  margin: 6px 0 12px;
  font-size: var(--fs-lead);
  font-weight: 600;
  color: var(--brand-deep);
}
.category-details__text {
  margin: 0;
  font-size: var(--fs-body);
  line-height: 1.55;
  color: var(--text-secondary);
  white-space: pre-line;
}
</style>
